import { z } from 'zod';

import { CLINICIAN_ROLE, MARKETPLACE_USER_ROLE, SUPPLIER_ROLE } from '~/types/auth';
import { zipCodeRegex } from '~/utils/validationRegex';

export const FirstNameFormSchema = z
  .string()
  .trim()
  .min(2, { error: 'Must be at least 2 characters long' })
  .max(50, { error: 'Cannot be more than 50 characters long' });

export const LastNameFormSchema = z
  .string()
  .trim()
  .min(1, { error: 'Must be at least 1 character long' })
  .max(50, { error: 'Cannot be more than 50 characters long' });

export const EmailSchema = z
  .email({ error: 'Must be a valid email address' })
  .trim()
  .max(100, { error: 'Cannot be more than 100 characters long' })
  .transform((val) => val.toLowerCase());

export const AddressSchema = z.object({
  city: z
    .string({
      error: (issue) => (issue.input === undefined ? 'City is required' : 'City is invalid'),
    })
    .min(2, {
      error: 'City is too short',
    })
    .transform((val) => val.trim()),
  complement: z
    .string({
      error: (issue) =>
        issue.input === undefined ? 'Complement is required' : 'Complement is invalid',
    })
    .max(100, {
      error: 'Complement is too long',
    })
    .optional()
    .nullable(),
  state: z
    .string({
      error: (issue) => (issue.input === undefined ? 'State is required' : 'State is invalid'),
    })
    .min(2, {
      error: 'State is too short',
    })
    .max(2, {
      error: 'State is too long',
    }),
  street: z
    .string({
      error: (issue) => (issue.input === undefined ? 'Street is required' : 'Street is invalid'),
    })
    .min(5, {
      error: 'Street is too short',
    })
    .max(50, {
      error: 'Street is too long',
    })
    .transform((val) => val.trim()),
  zipCode: z
    .string({
      error: (issue) =>
        issue.input === undefined ? 'Zip Code is required' : 'Zip Code is invalid',
    })
    .max(5, {
      error: 'Zip Code is too long',
    })
    .refine((value) => zipCodeRegex.test(value), {
      error: 'Zip Code is invalid',
    }),
});

const SELECTABLE_USER_ROLES = [SUPPLIER_ROLE, MARKETPLACE_USER_ROLE, CLINICIAN_ROLE] as const;
type SelectableUserRole = (typeof SELECTABLE_USER_ROLES)[number];
const ROLE_LABELS = {
  [SUPPLIER_ROLE]: 'Supplier',
  [MARKETPLACE_USER_ROLE]: 'Marketplace User',
  [CLINICIAN_ROLE]: 'Clinician',
} satisfies Record<SelectableUserRole, string>;
export const ROLE_OPTIONS = SELECTABLE_USER_ROLES.map((role) => ({
  value: role,
  label: ROLE_LABELS[role],
}));
export const RoleSchema = z.enum(SELECTABLE_USER_ROLES, {
  error: 'Role is required',
});
